/* eslint-disable no-use-before-define */
/**
 * TanStack Query hooks for Bulk Groups
 *
 * This file provides React Query hooks for fetching and mutating bulk group data.
 * These hooks replace the async methods in the Zustand bulk-groups store.
 *
 * Cross-store synchronization:
 * - getById populates products and media caches
 * - remove can also remove associated products from cache
 *
 * @module bulk-groups-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type BulkGroup, type BulkGroupCreate } from '../../entity/api/bulk-group';
import {
  createBulkGroup,
  getBulkGroupByBrandId,
  getBulkGroupById,
  getBulkGroupsList,
  removeBulkGroup,
  updateBulkGroup,
} from '../../sdk/bulk-group';
import { productsKeys, removeProductsByIdsFromCache } from './products-queries';

// ============ QUERY KEYS ============

export const bulkGroupsKeys = {
  all: ['bulk-groups'] as const,
  list: function () {
    return [...bulkGroupsKeys.all, 'list'] as const;
  },
  byId: function (id: string) {
    return [...bulkGroupsKeys.all, id] as const;
  },
  byBrandId: function (brandId: string) {
    return [...bulkGroupsKeys.all, 'brand', brandId] as const;
  },
  byOrgId: function (orgId: string) {
    return [...bulkGroupsKeys.all, 'org', orgId] as const;
  },
};

// ============ QUERIES ============

/**
 * Fetch all bulk groups list
 *
 * Replaces: getStores().bulkGroups.getState().list()
 */
export function useBulkGroupsList() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bulkGroupsKeys.list(),
    queryFn: async function () {
      const response = await getBulkGroupsList();
      const { bulkGroups } = response.data;

      // Cache each bulk group individually
      for (const bulkGroup of bulkGroups) {
        queryClient.setQueryData(bulkGroupsKeys.byId(bulkGroup._id), bulkGroup);
      }

      return bulkGroups;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch a bulk group by ID
 * Also populates products and media caches
 *
 * Replaces: getStores().bulkGroups.getState().getById(id)
 */
export function useBulkGroupById(id: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bulkGroupsKeys.byId(id ?? ''),
    queryFn: async function () {
      if (id == null) {
        throw new Error('Bulk Group ID is required');
      }
      const response = await getBulkGroupById(id);
      const { bulkGroup, products, medias } = response.data;

      // Cache products
      for (const product of products) {
        queryClient.setQueryData(productsKeys.byId(product._id), product);
      }

      // Also update the bulk group products list
      queryClient.setQueryData(productsKeys.byBulkGroupId(id), products);

      // Cache media - dynamic import to avoid circular dependencies
      if (medias != null && medias.length > 0) {
        import('./media-queries')
          .then(function ({ setMediasInCache }) {
            setMediasInCache(queryClient, medias);
          })
          .catch(function () {
            // media-queries might not exist yet during migration
          });
      }

      return bulkGroup;
    },
    enabled: id != null && id !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch bulk groups by brand ID
 *
 * Replaces: getStores().bulkGroups.getState().getByBrandId(brandId)
 */
export function useBulkGroupsByBrandId(brandId: string | undefined | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bulkGroupsKeys.byBrandId(brandId ?? ''),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getBulkGroupByBrandId(brandId);
      const { bulkGroups } = response.data;

      // Cache each bulk group individually
      for (const bulkGroup of bulkGroups) {
        queryClient.setQueryData(bulkGroupsKeys.byId(bulkGroup._id), bulkGroup);
      }

      return bulkGroups;
    },
    enabled: brandId != null && brandId !== '',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============ MUTATIONS ============

/**
 * Create a new bulk group
 *
 * Replaces: getStores().bulkGroups.getState().create(data, file)
 */
export function useCreateBulkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ data, file }: { data: BulkGroupCreate; file: File }) {
      const response = await createBulkGroup(data, file);
      return response;
    },
    onSuccess: function (bulkGroup) {
      // Cache the new bulk group
      queryClient.setQueryData(bulkGroupsKeys.byId(bulkGroup._id), bulkGroup);

      // Invalidate list queries
      void queryClient.invalidateQueries({ queryKey: bulkGroupsKeys.list() });
      void queryClient.invalidateQueries({
        queryKey: bulkGroupsKeys.byBrandId(bulkGroup.brandId),
      });
      void queryClient.invalidateQueries({
        queryKey: bulkGroupsKeys.byOrgId(bulkGroup.organizationId),
      });
    },
  });
}

/**
 * Update a bulk group
 *
 * Replaces: getStores().bulkGroups.getState().update(id, data)
 */
export function useUpdateBulkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, data }: { id: string; data: Partial<BulkGroup> }) {
      const response = await updateBulkGroup(id, data);
      return response.data;
    },
    onSuccess: function (bulkGroup, { id }) {
      // Update the bulk group in cache
      queryClient.setQueryData(bulkGroupsKeys.byId(id), bulkGroup);

      // Update in list caches
      updateBulkGroupInListCaches(queryClient, bulkGroup);
    },
  });
}

/**
 * Remove a bulk group
 * Also removes associated products from cache if shouldRemoveProducts is true
 *
 * Replaces: getStores().bulkGroups.getState().remove(id, shouldRemoveProducts)
 */
export function useRemoveBulkGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      id,
      shouldRemoveProducts,
    }: {
      id: string;
      shouldRemoveProducts: boolean;
    }) {
      const response = await removeBulkGroup(id, shouldRemoveProducts);
      return {
        id,
        shouldRemoveProducts,
        productsIdsToRemove: response.productsIdsToRemove,
      };
    },
    onSuccess: function ({ id, shouldRemoveProducts, productsIdsToRemove }) {
      // Get bulk group before removing
      const bulkGroup = queryClient.getQueryData<BulkGroup>(bulkGroupsKeys.byId(id));

      // Remove from cache
      queryClient.removeQueries({ queryKey: bulkGroupsKeys.byId(id) });

      // Remove associated products if requested
      if (shouldRemoveProducts && productsIdsToRemove?.length > 0) {
        removeProductsByIdsFromCache(queryClient, productsIdsToRemove);
      }

      // Also remove the products by bulk group ID query
      queryClient.removeQueries({
        queryKey: productsKeys.byBulkGroupId(id),
      });

      // Update list caches
      if (bulkGroup != null) {
        removeBulkGroupFromListCaches(queryClient, bulkGroup);
      }
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get bulk group from cache by ID
 */
export function getBulkGroupFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): BulkGroup | undefined {
  return queryClient.getQueryData<BulkGroup>(bulkGroupsKeys.byId(id));
}

/**
 * Get bulk groups list from cache
 */
export function getBulkGroupsListFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): BulkGroup[] | undefined {
  return queryClient.getQueryData<BulkGroup[]>(bulkGroupsKeys.list());
}

/**
 * Get bulk groups by brand ID from cache
 */
export function getBulkGroupsByBrandIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  brandId: string
): BulkGroup[] | undefined {
  return queryClient.getQueryData<BulkGroup[]>(bulkGroupsKeys.byBrandId(brandId));
}

/**
 * Get bulk groups by org ID from cache (client-side filter)
 */
export function getBulkGroupsByOrgIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): BulkGroup[] {
  const allBulkGroups = getBulkGroupsListFromCache(queryClient) ?? [];
  return allBulkGroups.filter(function (bg) {
    return bg.organizationId === orgId;
  });
}

/**
 * Update a bulk group in list caches
 */
function updateBulkGroupInListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  bulkGroup: BulkGroup
): void {
  // Update in list
  queryClient.setQueryData<BulkGroup[]>(bulkGroupsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.map(function (bg) {
      if (bg._id === bulkGroup._id) {
        return bulkGroup;
      }
      return bg;
    });
  });

  // Update in brand list
  queryClient.setQueryData<BulkGroup[]>(
    bulkGroupsKeys.byBrandId(bulkGroup.brandId),
    function (oldList) {
      if (oldList == null) {
        return oldList;
      }
      return oldList.map(function (bg) {
        if (bg._id === bulkGroup._id) {
          return bulkGroup;
        }
        return bg;
      });
    }
  );
}

/**
 * Remove a bulk group from list caches
 */
function removeBulkGroupFromListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  bulkGroup: BulkGroup
): void {
  // Remove from list
  queryClient.setQueryData<BulkGroup[]>(bulkGroupsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.filter(function (bg) {
      return bg._id !== bulkGroup._id;
    });
  });

  // Remove from brand list
  queryClient.setQueryData<BulkGroup[]>(
    bulkGroupsKeys.byBrandId(bulkGroup.brandId),
    function (oldList) {
      if (oldList == null) {
        return oldList;
      }
      return oldList.filter(function (bg) {
        return bg._id !== bulkGroup._id;
      });
    }
  );
}

/**
 * Invalidate all bulk group queries
 */
export function invalidateBulkGroups(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: bulkGroupsKeys.all });
}

/**
 * Invalidate a specific bulk group query
 */
export function invalidateBulkGroupById(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): void {
  void queryClient.invalidateQueries({ queryKey: bulkGroupsKeys.byId(id) });
}

// ============ IMPERATIVE FUNCTIONS ============

/**
 * Fetch bulk groups list and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand store for backward compatibility.
 */
export async function fetchBulkGroupsListImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<BulkGroup[]> {
  const response = await getBulkGroupsList();
  const { bulkGroups } = response.data;

  // Cache in TanStack Query
  queryClient.setQueryData(bulkGroupsKeys.list(), bulkGroups);

  // Also cache individual bulk groups by ID
  for (const bulkGroup of bulkGroups) {
    queryClient.setQueryData(bulkGroupsKeys.byId(bulkGroup._id), bulkGroup);
  }

  return bulkGroups;
}

/**
 * Fetch a bulk group by ID and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates products, media, and Zustand stores for backward compatibility.
 */
export async function fetchBulkGroupByIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Promise<BulkGroup> {
  const response = await getBulkGroupById(id);
  const { bulkGroup, products, medias } = response.data;

  // Cache bulk group in TanStack Query
  queryClient.setQueryData(bulkGroupsKeys.byId(id), bulkGroup);

  // Cache products
  for (const product of products) {
    queryClient.setQueryData(productsKeys.byId(product._id), product);
  }

  // Also update the bulk group products list
  queryClient.setQueryData(productsKeys.byBulkGroupId(id), products);

  // Cache media - dynamic import to avoid circular dependencies
  if (medias != null && medias.length > 0) {
    import('./media-queries')
      .then(function ({ setMediasInCache }) {
        setMediasInCache(queryClient, medias);
      })
      .catch(function () {
        // media-queries might not exist yet during migration
      });
  }

  return bulkGroup;
}

/**
 * Fetch bulk groups by brand ID and cache them (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also populates Zustand store for backward compatibility.
 */
export async function fetchBulkGroupsByBrandIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  brandId: string
): Promise<BulkGroup[]> {
  const response = await getBulkGroupByBrandId(brandId);
  const { bulkGroups } = response.data;

  // Cache in TanStack Query
  queryClient.setQueryData(bulkGroupsKeys.byBrandId(brandId), bulkGroups);

  // Also cache individual bulk groups by ID
  for (const bulkGroup of bulkGroups) {
    queryClient.setQueryData(bulkGroupsKeys.byId(bulkGroup._id), bulkGroup);
  }

  return bulkGroups;
}

/**
 * Set a bulk group in cache (for socket updates)
 */
export function setBulkGroupInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  bulkGroup: BulkGroup
): void {
  queryClient.setQueryData(bulkGroupsKeys.byId(bulkGroup._id), bulkGroup);
  updateBulkGroupInListCaches(queryClient, bulkGroup);
}
